/* VIVID — scenes and diagrams (dark edition). Loaded by the scene sections.
   - Flows: measures the boxes in [data-vh-flow] and draws curved lines between them
     (links listed in data-links as [from, to, kind, labelIndex, step]), then lets particles
     travel the main lines once they have drawn.
   - Scenes in view get .is-in (sequenced CSS takes it from there).
   - Night: a sticky scene scrubbed by scroll — clock, notes and meters follow progress;
     a Without / With Vivid switch tweens the meters between two illustrative curves.
   - Timing: the Before / Morning after switch.
   - Explorer: ingredient tabs on desktop, swipe progress on phones.
   Everything respects prefers-reduced-motion. */
(function () {
  if (window.VHScenes) return;
  window.VHScenes = true;

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const SVG = 'http://www.w3.org/2000/svg';
  const section = (sel, init, teardown) => (window.VH ? VH.section(sel, init, teardown) : document.querySelectorAll(sel).forEach(init));

  /* ---------- In view ---------- */
  const inIO = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (!e.isIntersecting) return;
      e.target.classList.add('is-in');
      e.target.dispatchEvent(new CustomEvent('vh:in'));
      inIO.unobserve(e.target);
    });
  }, { rootMargin: '0px 0px -15% 0px', threshold: 0.12 });
  const whenIn = (el) => (reduce.matches ? (el.classList.add('is-in'), el.dispatchEvent(new CustomEvent('vh:in'))) : inIO.observe(el));

  /* ---------- Flows ---------- */
  const rel = (el, box) => {
    const r = el.getBoundingClientRect();
    return { l: r.left - box.left, t: r.top - box.top, r: r.right - box.left, b: r.bottom - box.top, w: r.width, h: r.height };
  };
  const curve = (a, b) => {
    // Pick the facing edges: side by side -> horizontal S-curve, stacked -> vertical
    const acx = a.l + a.w / 2, acy = a.t + a.h / 2, bcx = b.l + b.w / 2, bcy = b.t + b.h / 2;
    let p0, p1, horizontal;
    // Straight across/down when the boxes overlap on the other axis (e.g. into a wide zone)
    const ty = acy > b.t + 12 && acy < b.b - 12 ? acy : bcy;
    const tx = acx > b.l + 12 && acx < b.r - 12 ? acx : bcx;
    if (b.l >= a.r - 2) { p0 = [a.r, acy]; p1 = [b.l, ty]; horizontal = true; }
    else if (b.r <= a.l + 2) { p0 = [a.l, acy]; p1 = [b.r, ty]; horizontal = true; }
    else if (b.t >= a.b - 2) { p0 = [acx, a.b]; p1 = [tx, b.t]; horizontal = false; }
    else { p0 = [acx, a.t]; p1 = [tx, b.b]; horizontal = false; }
    const k = horizontal ? (p1[0] - p0[0]) * 0.5 : (p1[1] - p0[1]) * 0.5;
    const c0 = horizontal ? [p0[0] + k, p0[1]] : [p0[0], p0[1] + k];
    const c1 = horizontal ? [p1[0] - k, p1[1]] : [p1[0], p1[1] - k];
    const d = `M${p0[0].toFixed(1)} ${p0[1].toFixed(1)} C${c0[0].toFixed(1)} ${c0[1].toFixed(1)} ${c1[0].toFixed(1)} ${c1[1].toFixed(1)} ${p1[0].toFixed(1)} ${p1[1].toFixed(1)}`;
    // Midpoint of the cubic (t = .5) for the label
    const mid = [0.125 * p0[0] + 0.375 * c0[0] + 0.375 * c1[0] + 0.125 * p1[0], 0.125 * p0[1] + 0.375 * c0[1] + 0.375 * c1[1] + 0.125 * p1[1]];
    return { d, mid, start: p0, end: p1, horizontal };
  };

  const drawFlow = (flow) => {
    const svg = flow.querySelector('.vh-flow__links');
    if (!svg) return;
    let links;
    try { links = JSON.parse(flow.dataset.links || '[]'); } catch (e) { return; }
    const labels = (flow.dataset.labels || '').split('|');
    const box = flow.getBoundingClientRect();
    svg.setAttribute('viewBox', `0 0 ${box.width.toFixed(1)} ${box.height.toFixed(1)}`);
    svg.replaceChildren();
    flow.querySelectorAll('.vh-flow-label').forEach((n) => n.remove());

    links.forEach(([from, to, kind, labelIndex, step], i) => {
      const a = flow.querySelector(`[data-node="${from}"]`), b = flow.querySelector(`[data-node="${to}"]`);
      if (!a || !b || !a.offsetParent || !b.offsetParent) return;
      const { d, mid, start, end, horizontal } = curve(rel(a, box), rel(b, box));
      const path = document.createElementNS(SVG, 'path');
      path.setAttribute('d', d);
      path.setAttribute('pathLength', '1');
      path.setAttribute('class', `vh-link${kind ? ' is-' + kind : ''}`);
      path.style.setProperty('--d', step ?? i);
      svg.appendChild(path);

      const dot = document.createElementNS(SVG, 'circle');
      dot.setAttribute('cx', end[0]); dot.setAttribute('cy', end[1]); dot.setAttribute('r', 3);
      dot.setAttribute('class', 'vh-link-dot');
      dot.style.setProperty('--d', step ?? i);
      svg.appendChild(dot);

      if (kind === 'main' && !reduce.matches) {
        for (let n = 0; n < 2; n++) {
          const c = document.createElementNS(SVG, 'circle');
          c.setAttribute('r', '2.6');
          c.setAttribute('class', 'vh-particle');
          const m = document.createElementNS(SVG, 'animateMotion');
          m.setAttribute('dur', '2.8s');
          m.setAttribute('repeatCount', 'indefinite');
          m.setAttribute('begin', `${(n * 1.4 + i * 0.35).toFixed(2)}s`);
          m.setAttribute('path', d);
          c.appendChild(m);
          svg.appendChild(c);
        }
      }

      const text = labelIndex != null && labelIndex !== '' ? labels[labelIndex] : '';
      // A label needs room: skip it on a level link too short to hold it (phones)
      const room = horizontal ? Math.abs(end[0] - start[0]) : Math.abs(end[1] - start[1]) + 40;
      if (text && room > 80) {
        const tag = document.createElement('span');
        tag.className = `vh-flow-label ${horizontal ? 'is-above' : 'is-beside'}`;
        tag.textContent = text;
        tag.style.left = `${mid[0]}px`;
        tag.style.top = `${mid[1]}px`;
        tag.style.setProperty('--d', step ?? i);
        flow.appendChild(tag);
      }
    });
  };

  section('[data-vh-flow]', (flow) => {
    let raf = 0;
    const redraw = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(() => drawFlow(flow)); };
    redraw();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(redraw);
    const ro = new ResizeObserver(redraw);
    ro.observe(flow);
    flow._vhRO = ro;
    flow.addEventListener('vh:in', () => setTimeout(() => flow.classList.add('is-flowing'), 2200), { once: true });
    whenIn(flow);
  }, (flow) => flow._vhRO && flow._vhRO.disconnect());

  section('[data-vh-inview-class]', whenIn);

  /* ---------- Night: scroll-scrubbed, Without / With Vivid ----------
     The night runs twice. First pass over the first 44% of the scroll (--p 0 → 1 drives the sky,
     the timeline and its dot in CSS; here it also runs the clock, picks the current note and
     paints the meters), then a short hold on the morning. At 50% the scene switches itself to
     With Vivid (unless the shopper picked a side) and the night starts again from 22:00, running
     to the morning by 92%; the last stretch is a short linger. Each meter carries two curves (data-curve, data-curve-with);
     the switch tweens between them (mix 0 → 1). English times read 10:00 pm; other languages 22:00. */
  const NIGHT_END = 0.44, REPLAY = 0.5, REPLAY_END = 0.92, AUTO_OFF = 0.48;
  const twelve = /^en/i.test(document.documentElement.lang || 'en');
  const hhmm = (hours) => {
    const h = Math.floor(hours) % 24, m = Math.round((hours % 1) * 60);
    if (!twelve) return { time: `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`, ampm: '' };
    return { time: `${h % 12 || 12}:${String(m).padStart(2, '0')}`, ampm: h < 12 ? 'am' : 'pm' };
  };
  const labelHour = (el) => {
    const t = hhmm(parseFloat(el.dataset.hour));
    if (!twelve) { el.textContent = el.hasAttribute('data-short') ? t.time.replace(/:\d+$/, ':00') : t.time; return; }
    el.textContent = el.hasAttribute('data-short') ? `${t.time.replace(/:00$/, '')} ${t.ampm}` : `${t.time} ${t.ampm}`;
  };
  const curveAt = (pts, p) => {
    const seg = (pts.length - 1) * p, i = Math.min(Math.floor(seg), pts.length - 2), f = seg - i;
    return pts[i] + (pts[i + 1] - pts[i]) * f;
  };
  section('[data-vh-night]', (scene) => {
    const track = scene.querySelector('.vh-night__track');
    const stops = [...scene.querySelectorAll('.vh-night__stop')];
    const clock = scene.querySelector('[data-night-clock]');
    const modeBtns = [...scene.querySelectorAll('[data-night-mode]')];
    if (!track || !stops.length) return;

    const parse = (s) => (s || '').split(',').map(Number).filter((n) => isFinite(n));
    const meters = [...scene.querySelectorAll('.vh-meter')].map((el) => {
      const off = parse(el.dataset.curve);
      const on = parse(el.dataset.curveWith);
      return { el, out: el.querySelector('.vh-meter__value'), off: off.length > 1 ? off : [100, 40], on: on.length > 1 ? on : (off.length > 1 ? off : [100, 40]) };
    });
    const ampm = scene.querySelector('[data-night-ampm]');
    scene.querySelectorAll('[data-hour]').forEach(labelHour);
    const isStatic = reduce.matches;
    let p = isStatic ? 1 : 0;   // the static list shows the morning values
    let picked = false, auto = false, before = scene.dataset.mode || 'without';
    let mix = scene.dataset.mode === 'with' ? 1 : 0;
    let mixRaf = 0, ticking = false, current = -1;

    const paintMeters = () => meters.forEach((m) => {
      const a = curveAt(m.off, p), v = a + (curveAt(m.on, p) - a) * mix;
      m.el.style.setProperty('--v', v.toFixed(1));
      if (m.out) m.out.textContent = `${Math.round(v)}%`;
      m.el.classList.toggle('is-low', v < 50);
    });

    const setMode = (mode) => {
      if (scene.dataset.mode === mode) return;
      scene.dataset.mode = mode;
      modeBtns.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.nightMode === mode)));
      const to = mode === 'with' ? 1 : 0;
      cancelAnimationFrame(mixRaf);
      if (reduce.matches) { mix = to; paintMeters(); return; }
      const from = mix, t0 = performance.now(), dur = 700;
      scene.classList.add('is-blending');
      const step = (t) => {
        const k = Math.min(1, (t - t0) / dur);
        mix = from + (to - from) * (1 - Math.pow(1 - k, 3));
        paintMeters();
        if (k < 1) mixRaf = requestAnimationFrame(step);
        else scene.classList.remove('is-blending');
      };
      mixRaf = requestAnimationFrame(step);
    };
    const onMode = (e) => { picked = true; auto = false; setMode(e.currentTarget.dataset.nightMode); };
    modeBtns.forEach((b) => b.addEventListener('click', onMode));
    scene._vhNightOff = () => { cancelAnimationFrame(mixRaf); modeBtns.forEach((b) => b.removeEventListener('click', onMode)); };

    if (isStatic) { scene.classList.add('is-static'); paintMeters(); return; }

    // The With Vivid lines only move while the scene is on screen
    const seen = new IntersectionObserver(([e]) => scene.classList.toggle('is-on-screen', e.isIntersecting));
    seen.observe(scene);
    const off = scene._vhNightOff;
    scene._vhNightOff = () => { off(); seen.disconnect(); };

    const startH = parseFloat(scene.dataset.start || 22), spanH = parseFloat(scene.dataset.span || 10);
    const update = () => {
      ticking = false;
      const r = track.getBoundingClientRect();
      const span = r.height - window.innerHeight;
      const raw = Math.min(1, Math.max(0, -r.top / (span || 1)));
      p = raw < REPLAY ? Math.min(1, raw / NIGHT_END) : Math.min(1, (raw - REPLAY) / (REPLAY_END - REPLAY));
      scene.style.setProperty('--p', p.toFixed(4));
      const hours = startH + spanH * p;
      const t = hhmm(Math.floor(hours * 4) / 4);   // quarter hours
      if (clock) clock.textContent = t.time;
      if (ampm) ampm.textContent = t.ampm;
      // Second pass: the same night again, with Vivid; back up past the hold and it goes back
      if (!picked && modeBtns.length) {
        if (raw >= REPLAY && !auto && scene.dataset.mode !== 'with') { before = scene.dataset.mode; auto = true; setMode('with'); }
        else if (raw < AUTO_OFF && auto) { auto = false; setMode(before); }
      }
      const idx = Math.min(stops.length - 1, Math.floor(p * stops.length * 0.999));
      if (idx !== current) {
        stops.forEach((s, i) => s.classList.toggle('is-current', i === idx));
        current = idx;
      }
      paintMeters();
    };
    const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    scene._vhNight = onScroll;
    update();
  }, (scene) => {
    if (scene._vhNightOff) scene._vhNightOff();
    if (scene._vhNight) { window.removeEventListener('scroll', scene._vhNight); window.removeEventListener('resize', scene._vhNight); }
  });

  /* ---------- Timing: before / after ---------- */
  section('[data-vh-timing]', (scene) => {
    const btns = [...scene.querySelectorAll('[data-mode-btn]')];
    const set = (mode) => {
      scene.dataset.mode = mode;
      btns.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.modeBtn === mode)));
    };
    btns.forEach((b) => b.addEventListener('click', () => set(b.dataset.modeBtn)));
    set(scene.dataset.mode || 'before');
    whenIn(scene);
  });

  /* ---------- Explorer: tabs + swipe progress ---------- */
  section('[data-vh-explorer]', (scene) => {
    const tabs = [...scene.querySelectorAll('[role="tab"]')];
    const panels = [...scene.querySelectorAll('[role="tabpanel"]')];
    const strip = scene.querySelector('.vh-explorer__panels');
    const bar = scene.querySelector('.vh-explorer__progress');
    const select = (i, focus) => {
      tabs.forEach((t, n) => {
        const on = n === i;
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
        if (panels[n]) {
          panels[n].hidden = !on;
          panels[n].classList.toggle('is-entering', on);
        }
      });
      if (focus) tabs[i].focus();
    };
    tabs.forEach((t, i) => {
      t.addEventListener('click', () => select(i));
      t.addEventListener('keydown', (e) => {
        const k = e.key;
        if (k === 'ArrowDown' || k === 'ArrowRight') { e.preventDefault(); select((i + 1) % tabs.length, true); }
        if (k === 'ArrowUp' || k === 'ArrowLeft') { e.preventDefault(); select((i - 1 + tabs.length) % tabs.length, true); }
      });
    });
    if (strip && bar) {
      strip.addEventListener('scroll', () => {
        const max = strip.scrollWidth - strip.clientWidth;
        bar.style.setProperty('--x', max > 0 ? (strip.scrollLeft / max).toFixed(3) : 0);
      }, { passive: true });
    }
    whenIn(scene);
  });
})();
